// Monaco as a controlled component (spec/dashboard.md section 10.8): one editor, one model per script path (each tab keeps
// its own undo stack), Ctrl+S saves. Before Monaco resolves it shows "Loading editor…"; when Monaco cannot load (or a
// test stubs the loader with a rejection) a plain text area takes its place, so the page always works.
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { loadMonaco, setModelTypings } from '../../extensions/monaco';
import { effectiveTheme } from '../../ui/theme';

type Monaco = typeof import('monaco-editor');
type Editor = import('monaco-editor').editor.IStandaloneCodeEditor;
type Model = import('monaco-editor').editor.ITextModel;

export interface CodeEditorHandle {
  /** runs Monaco's formatter; false when no formatter is available */
  format: () => Promise<boolean>;
}

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** the model's identity (one model per script) */
  path: string;
  language?: 'javascript';
  readOnly?: boolean;
  onSave?: () => void;
  /** the generated model types of the branch (`models.d.ts`) */
  modelTypes?: string;
  ref?: Ref<CodeEditorHandle>;
}

export function CodeEditor({ value, onChange, path, language = 'javascript', readOnly = false, onSave, modelTypes, ref }: CodeEditorProps) {
  const container = useRef<HTMLDivElement>(null);
  const [monaco, setMonaco] = useState<Monaco | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const editor = useRef<Editor | null>(null);
  const models = useRef(new Map<string, Model>());
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const valueRef = useRef(value);
  valueRef.current = value;

  useImperativeHandle(
    ref,
    () => ({
      format: async () => {
        const action = editor.current?.getAction('editor.action.formatDocument');
        if (!action) return false;
        await action.run();
        return true;
      },
    }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    loadMonaco().then(
      (m) => {
        if (!cancelled) setMonaco(m);
      },
      (e: unknown) => {
        if (!cancelled) setFailed(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // the editor itself: created once Monaco is there, disposed with its models on unmount
  useEffect(() => {
    if (!monaco || !container.current) return;
    const ed = monaco.editor.create(container.current, {
      language,
      readOnly,
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: 13,
      tabSize: 2,
      scrollBeyondLastLine: false,
      theme: effectiveTheme() === 'dark' ? 'vs-dark' : 'vs',
      model: null,
    });
    editor.current = ed;
    const sub = ed.onDidChangeModelContent(() => {
      const v = ed.getModel()?.getValue();
      if (v !== undefined && v !== valueRef.current) onChangeRef.current(v);
    });
    ed.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => onSaveRef.current?.());
    const owned = models.current;
    return () => {
      sub.dispose();
      ed.dispose();
      editor.current = null;
      for (const m of owned.values()) m.dispose();
      owned.clear();
    };
    // the language and read-only flag are applied below without recreating the editor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monaco]);

  // the model of the current path; the value pushed in when it changes from outside (Reset, Reload)
  useEffect(() => {
    const ed = editor.current;
    if (!monaco || !ed) return;
    let model = models.current.get(path);
    if (!model || model.isDisposed()) {
      model = monaco.editor.getModel(monaco.Uri.parse(path)) ?? monaco.editor.createModel(value, language, monaco.Uri.parse(path));
      models.current.set(path, model);
    }
    if (ed.getModel() !== model) ed.setModel(model);
    if (model.getValue() !== value) model.setValue(value);
  }, [monaco, path, value, language]);

  useEffect(() => {
    editor.current?.updateOptions({ readOnly });
  }, [monaco, readOnly]);

  useEffect(() => {
    if (monaco && modelTypes !== undefined) setModelTypings(monaco, modelTypes);
  }, [monaco, modelTypes]);

  if (failed !== null) {
    return (
      <div className="code-editor code-editor-fallback">
        <p className="code-editor-note muted small" role="status">
          The code editor could not be loaded ({failed}); a plain text editor is used.
        </p>
        <textarea
          aria-label="Script source"
          className="code-editor-textarea"
          spellCheck={false}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
              e.preventDefault();
              onSave?.();
            }
          }}
        />
      </div>
    );
  }
  return (
    <div className="code-editor">
      {!monaco && (
        <div className="code-editor-loading loading-line" role="status">
          <span className="spinner" aria-hidden="true" />
          Loading editor…
        </div>
      )}
      <div ref={container} className="code-editor-host" hidden={!monaco} />
    </div>
  );
}
